import React, { useState } from 'react';
import { Card, Input, Button, Space, Typography, Row, Col, Popconfirm } from 'antd';
import { DeleteOutlined, PlusOutlined, EditOutlined, SaveOutlined, CheckOutlined } from '@ant-design/icons';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { sanitizeHtml } from '../utils/sanitize';

const { Text, Title } = Typography;

const QuestionCard = ({ question, questionIndex, onQuestionChange, onDeleteQuestion, isEditing = true }) => {
  const [editingAnswerIndex, setEditingAnswerIndex] = useState(null);
  const [tempAnswerText, setTempAnswerText] = useState('');
  const [editingExplanationIndex, setEditingExplanationIndex] = useState(null);
  const [explanationEditorState, setExplanationEditorState] = useState(EditorState.createEmpty());

  // Helper function to create EditorState from HTML
  const createEditorStateFromHtml = (html) => {
    if (!html) return EditorState.createEmpty();
    const contentBlock = htmlToDraft(html);
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  };

  // Helper function to convert EditorState to HTML
  const getHtmlFromEditorState = (editorState) => {
    return draftToHtml(convertToRaw(editorState.getCurrentContent()));
  };

  const handleQuestionTextChange = (text) => {
    onQuestionChange(question.id, { ...question, text });
  };

  const handleVideoUrlChange = (videoUrl) => {
    onQuestionChange(question.id, { ...question, videoUrl });
  };

  const handleAddAnswer = () => {
    const newAnswer = {
      id: Date.now(),
      text: '',
      isCorrect: false,
      explanation: ''
    };
    const updatedAnswers = [...(question.answers || []), newAnswer];
    onQuestionChange(question.id, { ...question, answers: updatedAnswers });
  };

  const handleAnswerTextChange = (answerIndex, text) => {
    const updatedAnswers = question.answers.map((answer, index) =>
      index === answerIndex ? { ...answer, text } : answer
    );
    onQuestionChange(question.id, { ...question, answers: updatedAnswers });
  };

  const handleAnswerExplanationChange = (answerIndex, explanation) => {
    const updatedAnswers = question.answers.map((answer, index) =>
      index === answerIndex ? { ...answer, explanation } : answer
    );
    onQuestionChange(question.id, { ...question, answers: updatedAnswers });
  };

  const handleDeleteAnswer = (answerIndex) => {
    const updatedAnswers = question.answers.filter((_, index) => index !== answerIndex);
    onQuestionChange(question.id, { ...question, answers: updatedAnswers });
  };

  const handleMarkCorrect = (answerIndex) => {
    const updatedAnswers = question.answers.map((answer, index) =>
      ({ ...answer, isCorrect: index === answerIndex })
    );
    onQuestionChange(question.id, { ...question, answers: updatedAnswers });
  };

  const startEditingAnswer = (answerIndex, currentText) => {
    setEditingAnswerIndex(answerIndex);
    setTempAnswerText(currentText);
  };

  const saveAnswerEdit = (answerIndex) => {
    handleAnswerTextChange(answerIndex, tempAnswerText);
    setEditingAnswerIndex(null);
    setTempAnswerText('');
  };

  const cancelAnswerEdit = () => {
    setEditingAnswerIndex(null);
    setTempAnswerText('');
  };

  const startEditingExplanation = (answerIndex, currentExplanation) => {
    setEditingExplanationIndex(answerIndex);
    setExplanationEditorState(createEditorStateFromHtml(currentExplanation || ''));
  };

  const saveExplanationEdit = (answerIndex) => {
    const htmlContent = getHtmlFromEditorState(explanationEditorState);
    handleAnswerExplanationChange(answerIndex, htmlContent);
    setEditingExplanationIndex(null);
    setExplanationEditorState(EditorState.createEmpty());
  };

  const cancelExplanationEdit = () => {
    setEditingExplanationIndex(null);
    setExplanationEditorState(EditorState.createEmpty());
  };

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Text strong>Question {questionIndex + 1}</Text>
          </Col>
          <Col>
            {isEditing && (
              <Popconfirm
                title="Delete this question?"
                onConfirm={() => onDeleteQuestion(question.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="text" icon={<DeleteOutlined />} size="small" danger />
              </Popconfirm>
            )}
          </Col>
        </Row>
      }
      style={{ marginBottom: 24 }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Question Text:
          </Text>
          {isEditing ? (
            <Input.TextArea
              value={question.text || ''}
              onChange={(e) => handleQuestionTextChange(e.target.value)}
              placeholder="Enter your question..."
              rows={3}
            />
          ) : (
            <Title level={4}>{question.text || 'No question text'}</Title>
          )}
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            YouTube Video (optional):
          </Text>
          {isEditing ? (
            <Input
              value={question.videoUrl || ''}
              onChange={(e) => handleVideoUrlChange(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          ) : (
            <Text>{question.videoUrl || 'No video link provided'}</Text>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Paste a public YouTube link to show a video instead of the question image.
          </Text>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Text strong>Answers:</Text>
            {isEditing && (
              <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddAnswer} size="small">
                Add Answer
              </Button>
            )}
          </div>

          {question.answers && question.answers.length > 0 ? (
            question.answers.map((answer, answerIndex) => (
              <Card
                key={answer.id || answerIndex}
                size="small"
                style={{
                  marginBottom: 12,
                  border: answer.isCorrect ? '2px solid #52c41a' : '1px solid #d9d9d9',
                  backgroundColor: answer.isCorrect ? '#f6ffed' : 'white'
                }}
                extra={
                  isEditing && (
                    <Popconfirm
                      title="Delete this answer?"
                      onConfirm={() => handleDeleteAnswer(answerIndex)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button type="text" icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                  )
                }
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {isEditing ? (
                    editingAnswerIndex === answerIndex ? (
                      <Space>
                        <Input
                          value={tempAnswerText}
                          onChange={(e) => setTempAnswerText(e.target.value)}
                          placeholder="Enter answer text..."
                          onPressEnter={() => saveAnswerEdit(answerIndex)}
                        />
                        <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => saveAnswerEdit(answerIndex)}>
                          Save
                        </Button>
                        <Button size="small" onClick={cancelAnswerEdit}>
                          Cancel
                        </Button>
                      </Space>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text onClick={() => startEditingAnswer(answerIndex, answer.text)} style={{ cursor: 'pointer', flex: 1 }}>
                          {answer.text || 'Click to add answer text...'}
                        </Text>
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditingAnswer(answerIndex, answer.text)} />
                      </div>
                    )
                  ) : (
                    <Text>{answer.text}</Text>
                  )}

                  {isEditing && (
                    <Button
                      type={answer.isCorrect ? "primary" : "default"}
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={() => handleMarkCorrect(answerIndex)}
                      style={{
                        backgroundColor: answer.isCorrect ? '#52c41a' : undefined,
                        borderColor: answer.isCorrect ? '#52c41a' : undefined
                      }}
                    >
                      {answer.isCorrect ? 'Correct Answer' : 'Mark as Correct'}
                    </Button>
                  )}

                  {answer.isCorrect && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                      <Text strong style={{ display: 'block', marginBottom: 8, color: '#52c41a' }}>
                        ✓ Explanation:
                      </Text>
                      {isEditing ? (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', minHeight: '150px' }}>
                            <Editor
                              editorState={editingExplanationIndex === answerIndex ? explanationEditorState : createEditorStateFromHtml(answer.explanation || '')}
                              onEditorStateChange={(newState) => {
                                if (editingExplanationIndex !== answerIndex) {
                                  setEditingExplanationIndex(answerIndex);
                                }
                                setExplanationEditorState(newState);
                              }}
                              onBlur={() => {
                                if (editingExplanationIndex === answerIndex) {
                                  const htmlContent = getHtmlFromEditorState(explanationEditorState);
                                  handleAnswerExplanationChange(answerIndex, htmlContent);
                                  setEditingExplanationIndex(null);
                                }
                              }}
                              wrapperClassName="explanation-editor-wrapper"
                              editorClassName="explanation-editor"
                              toolbarClassName="explanation-editor-toolbar"
                              placeholder="Enter explanation for why this answer is correct..."
                              toolbar={{
                                options: ['inline', 'list', 'link'],
                                inline: {
                                  options: ['bold', 'italic', 'underline'],
                                },
                                list: {
                                  options: ['unordered', 'ordered'],
                                },
                              }}
                              editorStyle={{ padding: '8px 12px', minHeight: '100px' }}
                            />
                          </div>
                        </Space>
                      ) : (
                        answer.explanation && (
                          <div
                            className="rich-text-content"
                            style={{ padding: '8px 12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f', color: '#389e0d' }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(answer.explanation) }}
                          />
                        )
                      )}
                    </div>
                  )}
                </Space>
              </Card>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '32px', border: '2px dashed #d9d9d9', borderRadius: '8px', backgroundColor: '#fafafa' }}>
              <Text type="secondary">No answers added yet</Text>
              {isEditing && (
                <div style={{ marginTop: 8 }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAnswer}>
                    Add First Answer
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Space>
    </Card>
  );
};

export default QuestionCard;